import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessAgreementPageQuery,
  PerformanceProcessAgreementSignAsEmployeeDocument,
  PerformanceProcessAgreementSignAsManagerDocument,
  PerformanceProcessAgreementUnsignAsEmployeeDocument,
  PerformanceProcessAgreementUnsignAsManagerDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePageTransactionalActions } from 'modules/layout/components/ResponsivePage'
import { signedBy } from 'modules/performance_process/lib/agreementUtils'

type Agreement = NonNullable<PerformanceProcessAgreementPageQuery['performance']['agreement']>
type Action = ResponsivePageTransactionalActions['primary']

type Role = 'target' | 'manager'
function isRole(agreement: Agreement, currentAccessId: number): Role | undefined {
  if (agreement.target?.access?.id === currentAccessId) return 'target'
  if (agreement._policies?.signAsManager.can || agreement._policies?.unsignAsManager.can)
    return 'manager'
}

export const useBuildTransactionalActions = ({
  agreement,
  currentAccessId,
}: {
  agreement?: Agreement | null
  currentAccessId?: number
}): ResponsivePageTransactionalActions | undefined => {
  const { openDialog } = useConfirmationDialog()

  const [signAsManager] = useMutation(PerformanceProcessAgreementSignAsManagerDocument, {
    extractorFn: (data) => data?.performanceMutations?.signAsManagerPerformanceAgreement,
  })

  const [signAsEmployee] = useMutation(PerformanceProcessAgreementSignAsEmployeeDocument, {
    extractorFn: (data) => data?.performanceMutations?.signAsEmployeePerformanceAgreement,
  })

  const [unsignAsManager] = useMutation(PerformanceProcessAgreementUnsignAsManagerDocument, {
    extractorFn: (data) => data?.performanceMutations?.unsignAsManagerPerformanceAgreement,
  })

  const [unsignAsEmployee] = useMutation(PerformanceProcessAgreementUnsignAsEmployeeDocument, {
    extractorFn: (data) => data?.performanceMutations?.unsignAsEmployeePerformanceAgreement,
  })

  if (!agreement || !currentAccessId) return undefined

  const unsignAsTargetAction: Action = {
    label: i18n.t('performance_process.agreements.agreement.unsign'),
    onClick: () =>
      openDialog({
        title: i18n.t('performance_process.agreements.unsign_dialog.title'),
        description: i18n.t('performance_process.agreements.unsign_dialog.content'),
        illustration: 'signature',
        onConfirm: () =>
          unsignAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const unsignAsTargetForManagerAction: Action = {
    label: i18n.t('performance_process.agreements.agreement.unsign_for_target', {
      name: agreement.target?.access?.firstName,
    }),
    onClick: () =>
      openDialog({
        title: i18n.t('performance_process.agreements.unsign_dialog_target.title', {
          name: agreement.target?.access?.firstName,
        }),
        description: i18n.t('performance_process.agreements.unsign_dialog_target.content'),
        illustration: 'signature',
        onConfirm: () =>
          unsignAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const unsignAsManagerAction: Action = {
    label: i18n.t('performance_process.agreements.agreement.unsign'),
    onClick: () =>
      openDialog({
        title: i18n.t('performance_process.agreements.unsign_dialog.title'),
        description: i18n.t('performance_process.agreements.unsign_dialog.content'),
        illustration: 'signature',
        onConfirm: () =>
          unsignAsManager({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const signAsTargetAction: Action = {
    label: i18n.t('performance_process.agreements.agreement.sign'),
    onClick: () =>
      openDialog({
        title: i18n.t('performance_process.agreements.signature_dialog.title'),
        description: i18n.t('performance_process.agreements.signature_dialog.content'),
        illustration: 'signature',
        confirmLabel: i18n.t('performance_process.agreements.agreement.sign_confirm'),
        onConfirm: () =>
          signAsEmployee({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  const signAsManagerAction: Action = {
    label: i18n.t('performance_process.agreements.agreement.sign'),
    onClick: () =>
      openDialog({
        title: i18n.t('performance_process.agreements.signature_dialog.title'),
        description: i18n.t('performance_process.agreements.signature_dialog.content'),
        illustration: 'signature',
        confirmLabel: i18n.t('performance_process.agreements.agreement.sign_confirm'),
        onConfirm: () =>
          signAsManager({
            variables: {
              agreementId: agreement.id,
            },
          }),
      }),
  }

  if (agreement?.targetSignedAt && agreement?.managerSignedAt) {
    return undefined
  }

  const signedByText = signedBy(agreement, currentAccessId)

  const status: ResponsivePageTransactionalActions['status'] | undefined = signedByText
    ? {
        icon: 'check',
        text: signedByText,
      }
    : undefined

  const role = isRole(agreement, currentAccessId)

  if (role === 'manager') {
    let secondary: Action | undefined = undefined

    if (agreement?._policies?.unsignAsTarget.can && agreement?.targetSignedAt) {
      secondary = unsignAsTargetForManagerAction
    }

    if (agreement?._policies?.unsignAsManager.can && agreement?.managerSignedAt) {
      return {
        status,
        secondary,
        primary: unsignAsManagerAction,
      }
    }

    if (agreement?._policies?.signAsManager.can) {
      return {
        status,
        secondary,
        primary: signAsManagerAction,
      }
    }
  }

  if (role === 'target') {
    if (agreement?._policies?.unsignAsTarget.can && agreement?.targetSignedAt) {
      return {
        status,
        primary: unsignAsTargetAction,
      }
    }

    if (agreement?._policies?.signAsTarget.can) {
      return {
        status,
        primary: signAsTargetAction,
      }
    }
  }
}
